<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import EmptyState from '../../../../components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import EmailInboxFinish from './channels/emailChannels/EmailInboxFinish.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const WHATSAPP_API_STATUS_POLL_INTERVAL = 5000;
const WHATSAPP_API_QR_REFRESH_INTERVAL = 40000;

const qrCodes = reactive({
  whatsapp: '',
  messenger: '',
  telegram: '',
  bale: '',
  rubika: '',
});

const whatsappApiSetup = reactive({
  pairingCode: '',
  isConnected: false,
  isRefreshingQr: false,
  isLoadingQr: false,
});

let whatsappApiStatusPoller;
let whatsappApiQrPoller;

const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](route.params.inbox_id)
);

// Use useInbox composable with the inbox ID
const {
  isAWhatsAppCloudChannel,
  isATwilioChannel,
  isASmsInbox,
  isALineChannel,
  isAnEmailChannel,
  isAWhatsAppChannel,
  isAFacebookInbox,
  isATelegramChannel,
  isABaleChannel,
  isARubikaChannel,
  isATwilioWhatsAppChannel,
} = useInbox(route.params.inbox_id);

const hasDuplicateInstagramInbox = computed(() => {
  const instagramId = currentInbox.value.instagram_id;
  const facebookInbox =
    store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId);

  return (
    currentInbox.value.channel_type === INBOX_TYPES.INSTAGRAM && facebookInbox
  );
});

const shouldShowWhatsAppWebhookDetails = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source !== 'embedded_signup'
  );
});

const isWhatsAppEmbeddedSignup = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source === 'embedded_signup'
  );
});

const isWhatsappApiEvolutionInbox = computed(() => {
  return (
    currentInbox.value.channel_type === INBOX_TYPES.API &&
    currentInbox.value.additional_attributes?.provider === 'evolution'
  );
});

const whatsappQrInstruction = computed(() => {
  if (isWhatsappApiEvolutionInbox.value) {
    return t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.QR_CODE.SUBTITLE');
  }

  return t('INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION');
});

const isTakeMeThereDisabled = computed(() => {
  return isWhatsappApiEvolutionInbox.value && !whatsappApiSetup.isConnected;
});

const message = computed(() => {
  if (isATwilioChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isASmsInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.SMS.BANDWIDTH.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isALineChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAWhatsAppCloudChannel.value && shouldShowWhatsAppWebhookDetails.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (currentInbox.value.web_widget_script) {
    return t('INBOX_MGMT.FINISH.WEBSITE_SUCCESS');
  }

  if (isWhatsAppEmbeddedSignup.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION'
    )}`;
  }

  return t('INBOX_MGMT.FINISH.MESSAGE');
});

async function generateQRCode(platform, identifier) {
  if (!identifier || !identifier.trim()) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid identifier for ${platform} QR code`);
    return;
  }

  try {
    const platformUrls = {
      whatsapp: id => `https://wa.me/${id}`,
      messenger: id => `https://m.me/${id}`,
      telegram: id => `https://t.me/${id}`,
      bale: id => `https://bale.ai/${id}`,
      rubika: id => `https://rubika.ir/${id}`,
    };

    const url = platformUrls[platform](identifier);
    const qrDataUrl = await QRCode.toDataURL(url);
    qrCodes[platform] = qrDataUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error generating ${platform} QR code:`, error);
    qrCodes[platform] = '';
  }
}

async function generateQRCodes() {
  if (!currentInbox.value) return;

  // WhatsApp (both Cloud and Twilio)
  if (currentInbox.value.phone_number && isAWhatsAppChannel.value) {
    // For Twilio WhatsApp, phone_number format is "whatsapp:+1234567890"
    // Extract just the phone number part for QR code generation
    const phoneNumber = currentInbox.value.phone_number.replace(
      'whatsapp:',
      ''
    );
    await generateQRCode('whatsapp', phoneNumber);
  }

  // Facebook Messenger
  if (currentInbox.value.page_id && isAFacebookInbox.value) {
    await generateQRCode('messenger', currentInbox.value.page_id);
  }

  // Telegram
  if (isATelegramChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('telegram', currentInbox.value.bot_name);
  }

  // Bale
  if (isABaleChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('bale', currentInbox.value.bot_name);
  }

  // Rubika
  if (isARubikaChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('rubika', currentInbox.value.bot_name);
  }
}

async function buildQrDataUrl(code = '') {
  if (!code) {
    return '';
  }

  if (code.startsWith('data:image')) {
    return code;
  }

  try {
    return await QRCode.toDataURL(code);
  } catch (error) {
    return `data:image/png;base64,${code}`;
  }
}

function stopWhatsappApiPolling() {
  if (whatsappApiStatusPoller) {
    clearInterval(whatsappApiStatusPoller);
    whatsappApiStatusPoller = null;
  }

  if (whatsappApiQrPoller) {
    clearInterval(whatsappApiQrPoller);
    whatsappApiQrPoller = null;
  }
}

async function pollWhatsappApiSetup({ withQr = false } = {}) {
  if (!isWhatsappApiEvolutionInbox.value) {
    return;
  }

  try {
    const response = await store.dispatch('inboxes/getWhatsappApiSetupStatus', {
      inboxId: route.params.inbox_id,
      withQr,
    });

    whatsappApiSetup.isConnected =
      response.done === true || response.state === 'open';

    if (response.qrcode?.code) {
      qrCodes.whatsapp = await buildQrDataUrl(response.qrcode.code);
    }

    whatsappApiSetup.pairingCode = response.qrcode?.pairing_code || '';

    if (whatsappApiSetup.isConnected) {
      stopWhatsappApiPolling();
      router.replace({
        name: 'inbox_dashboard',
        params: { inboxId: route.params.inbox_id },
      });
    }
  } catch (error) {
    // Ignore transient polling errors and continue retrying.
  }
}

async function refreshWhatsappApiQr() {
  if (whatsappApiSetup.isRefreshingQr) {
    return;
  }

  whatsappApiSetup.isRefreshingQr = true;
  try {
    await pollWhatsappApiSetup({ withQr: true });
  } finally {
    whatsappApiSetup.isRefreshingQr = false;
  }
}

async function startWhatsappApiPolling() {
  if (!isWhatsappApiEvolutionInbox.value || whatsappApiStatusPoller) {
    return;
  }

  whatsappApiSetup.isLoadingQr = true;
  await pollWhatsappApiSetup({ withQr: true });
  whatsappApiSetup.isLoadingQr = false;

  whatsappApiStatusPoller = setInterval(() => {
    pollWhatsappApiSetup({ withQr: false });
  }, WHATSAPP_API_STATUS_POLL_INTERVAL);
  whatsappApiQrPoller = setInterval(() => {
    pollWhatsappApiSetup({ withQr: true });
  }, WHATSAPP_API_QR_REFRESH_INTERVAL);
}

// Watch for currentInbox changes and regenerate QR codes when available
watch(
  currentInbox,
  newInbox => {
    if (newInbox) {
      generateQRCodes();
      if (isWhatsappApiEvolutionInbox.value) {
        startWhatsappApiPolling();
      } else {
        stopWhatsappApiPolling();
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  generateQRCodes();
  if (isWhatsappApiEvolutionInbox.value) {
    startWhatsappApiPolling();
  }
});

onBeforeUnmount(() => {
  stopWhatsappApiPolling();
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <DuplicateInboxBanner
      v-if="hasDuplicateInstagramInbox"
      :content="$t('INBOX_MGMT.ADD.INSTAGRAM.NEW_INBOX_SUGGESTION')"
    />
    <EmptyState
      :title="$t('INBOX_MGMT.FINISH.TITLE')"
      :message="isAnEmailChannel && !currentInbox.provider ? '' : message"
      :button-text="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
    >
      <div class="w-full text-center">
        <div class="my-4 mx-auto max-w-[70%]">
          <woot-code
            v-if="currentInbox.web_widget_script"
            :script="currentInbox.web_widget_script"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isATwilioWhatsAppChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div
          v-if="shouldShowWhatsAppWebhookDetails"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <p class="mt-8 font-medium text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_URL') }}
          </p>
          <woot-code lang="html" :script="currentInbox.callback_webhook_url" />
          <p class="mt-8 font-medium text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_VERIFICATION_TOKEN'
              )
            }}
          </p>
          <woot-code
            lang="html"
            :script="currentInbox.provider_config.webhook_verify_token"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isALineChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isASmsInbox"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <EmailInboxFinish
          v-if="isAnEmailChannel && !currentInbox.provider"
          :inbox="currentInbox"
          :inbox-id="$route.params.inbox_id"
        />
        <!-- Evolution API WhatsApp: show loading or QR -->
        <div
          v-if="isWhatsappApiEvolutionInbox"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ whatsappQrInstruction }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.QR_CODE.LINKED_DEVICES_INSTRUCTION'
              )
            }}
          </p>
          <p
            v-if="!whatsappApiSetup.isConnected"
            class="text-xs text-n-slate-10"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.QR_CODE.WAITING') }}
          </p>
          <p
            v-if="whatsappApiSetup.pairingCode"
            class="text-xs font-medium text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.QR_CODE.PAIRING_CODE', {
                code: whatsappApiSetup.pairingCode,
              })
            }}
          </p>
          <!-- Loading skeleton while QR is being fetched -->
          <div
            v-if="whatsappApiSetup.isLoadingQr"
            class="rounded-lg shadow outline-1 outline-n-strong outline size-48 flex items-center justify-center bg-n-alpha-black2"
          >
            <span
              class="i-lucide-loader-circle animate-spin size-8 text-n-slate-9"
            />
          </div>
          <!-- QR code image once available -->
          <div
            v-else-if="qrCodes.whatsapp"
            class="rounded-lg shadow outline-1 outline-n-strong outline"
          >
            <img
              :src="qrCodes.whatsapp"
              :alt="$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.QR_CODE.ALT')"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
          <NextButton
            :is-loading="whatsappApiSetup.isRefreshingQr"
            :disabled="
              whatsappApiSetup.isRefreshingQr || whatsappApiSetup.isLoadingQr
            "
            outline
            slate
            :label="$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.QR_CODE.REFRESH')"
            @click="refreshWhatsappApiQr"
          />
        </div>
        <!-- Non-Evolution WhatsApp QR -->
        <div
          v-else-if="isAWhatsAppChannel && qrCodes.whatsapp"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ whatsappQrInstruction }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.whatsapp"
              :alt="$t('INBOX_MGMT.ADD.WHATSAPP_API_CHANNEL.QR_CODE.ALT')"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isAFacebookInbox && qrCodes.messenger"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.MESSENGER_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.messenger"
              alt="Messenger QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isATelegramChannel && qrCodes.telegram"
          class="flex flex-col gap-4 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.TELEGRAM_QR_INSTRUCTION') }}
          </p>

          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.telegram"
              alt="Telegram QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isABaleChannel && qrCodes.bale"
          class="flex flex-col gap-4 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{
              $t(
                'INBOX_MGMT.FINISH.BALE_QR_INSTRUCTION',
                'Scan this code to connect your Bale bot'
              )
            }}
          </p>

          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.bale"
              alt="Bale QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isARubikaChannel && qrCodes.rubika"
          class="flex flex-col gap-4 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.RUBIKA_QR_INSTRUCTION') }}
          </p>

          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.rubika"
              alt="Rubika QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>

        <div class="flex gap-2 justify-center mt-4">
          <router-link
            :to="{
              name: 'settings_inbox_show',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              outline
              slate
              :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
            />
          </router-link>
          <router-link
            :to="{
              name: 'inbox_dashboard',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              :disabled="isTakeMeThereDisabled"
              solid
              teal
              :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
            />
          </router-link>
        </div>
      </div>
    </EmptyState>
  </div>
</template>
